import type { FormV2FieldValue, HubSpotFormInstance } from './types';

// Standard HubSpot object types and their objectTypeIds. Callers reference
// fields by object type + property name instead of raw ids, so a form spanning
// multiple objects (e.g. Contact + Company) is unambiguous. IDs are stable
// across HubSpot; custom objects are addressed by passing their raw id
// ('2-7381234') as the key.
export const PORTAL_FORM_OBJECT_TYPES = {
  CONTACT: '0-1',
  COMPANY: '0-2',
  DEAL: '0-3',
  TICKET: '0-5',
} as const;

export type PortalFormObjectType = keyof typeof PORTAL_FORM_OBJECT_TYPES;

// Nested prefill map keyed by object type, then by property name. Known object
// types (CONTACT, COMPANY, …) are suggested by the type; a raw objectTypeId
// string (e.g. '2-7381234') is also accepted for custom objects.
export type FormPrefill = Partial<
  Record<PortalFormObjectType | (string & {}), Record<string, FormV2FieldValue>>
>;

// Map a known object-type name to its id; pass through a raw id unchanged.
function toObjectTypeId(objectType: string): string {
  return (
    (PORTAL_FORM_OBJECT_TYPES as Record<string, string>)[objectType] ??
    objectType
  );
}

// Build the v2 full property reference ('{objectTypeId}/{property}', e.g.
// '0-1/email') from an object type (name or raw id) and property name.
export function toFieldReference(objectType: string, property: string): string {
  return `${toObjectTypeId(objectType)}/${property}`;
}

// Apply a nested prefill map to either form version once the form is ready.
// v2 keys fields by the full reference ('0-1/email'); we build it from the
// declared object type and only set fields that actually exist on the form
// (setFieldValue throws synchronously for an unknown reference). v1 is legacy
// and Contact-centric — it keys by bare property name and has no object-type
// concept, so only CONTACT properties are honored there.
export async function applyPrefill(
  instance: HubSpotFormInstance,
  prefill: FormPrefill
): Promise<void> {
  if (instance.version === 1) {
    const contactProps = prefill.CONTACT ?? {};
    for (const [property, value] of Object.entries(contactProps)) {
      if (instance.hasField(property)) {
        instance.setFieldValue(property, value);
      }
    }
    return;
  }

  const pairs = await instance.getFormFieldValues();
  const availableRefs = new Set(pairs.map((pair) => pair.name));

  for (const [objectType, props] of Object.entries(prefill)) {
    if (!props) continue;
    for (const [property, value] of Object.entries(props)) {
      const ref = toFieldReference(objectType, property);
      if (availableRefs.has(ref)) {
        instance.setFieldValue(ref, value);
      }
    }
  }
}
